import { useEffect } from 'react'
import styled from '@emotion/styled'
import {
  ISOString,
  OrderPrep as OrderPrepType,
  PrepStatus,
  ServiceType,
} from '@open-tender/types'
import { isoToDateStr, parseIsoToDate } from '@open-tender/utils'
import { fetchCustomerOrder, useAppDispatch } from '@open-tender/cloud'
import OrderProgress from './OrderProgress'
import Heading from 'components/Heading'

const OrderPrepView = styled.div`
  display: flex;
  justify-content: center;
  margin: 3rem 0;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    justify-content: flex-start;
    margin: 3rem 0;
  }
`

const OrderPrepContainer = styled.div`
  position: relative;
  padding: 0 0 0 7rem;
  margin: 0;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding: 0 0 0 5rem;
  }
`

const OrderPrepProgress = styled.div`
  position: absolute;
  top: 2rem;
  left: 0;
  bottom: 3rem;
  width: 7rem;
  display: flex;
  justify-content: center;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    width: 5rem;
    justify-content: flex-start;
    padding: 0 0 0 1.5rem;
  }
`

const OrderPrepStepView = styled.div`
  height: 5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const OrderPrepStepTitleActive = styled(Heading)`
  font-size: ${(props) => props.theme.fonts.sizes.small};
`

const OrderPrepStepTitle = styled.p`
  font-size: ${(props) => props.theme.fonts.sizes.small};
`

const OrderPrepStepSubtitle = styled.p`
  margin: 0.5rem 0 0;
  font-size: ${(props) => props.theme.fonts.sizes.xSmall};
  color: ${(props) => props.theme.colors.tertiary};
`

const formatDate = (timestamp: ISOString | null) => {
  if (!timestamp) return ' '
  return isoToDateStr(timestamp, null, 'MMM d, yyyy @ h:mma')
    .replace('PM', 'pm')
    .replace('AM', 'am')
}

const OrderPrepStep = ({
  title,
  timestamp,
  isActive,
}: {
  title: string
  timestamp: ISOString | null
  isActive?: boolean
}) => {
  return (
    <OrderPrepStepView>
      <div>
        {isActive ? (
          <OrderPrepStepTitleActive as="p">{title}</OrderPrepStepTitleActive>
        ) : (
          <OrderPrepStepTitle>{title}</OrderPrepStepTitle>
        )}
        <OrderPrepStepSubtitle>{formatDate(timestamp)}</OrderPrepStepSubtitle>
      </div>
    </OrderPrepStepView>
  )
}

const statusPercentages: Record<PrepStatus, number> = {
  TODO: 25,
  IN_PROGRESS: 50,
  DONE: 75,
  COMPLETED: 100,
  FULFILLED: 100,
}

const statusPercentagesFulfilled: Record<PrepStatus, number> = {
  TODO: 20,
  IN_PROGRESS: 40,
  DONE: 60,
  COMPLETED: 80,
  FULFILLED: 100,
}

const OrderPrep = ({
  orderId,
  orderPrep,
  serviceType,
}: {
  orderId: number
  orderPrep: OrderPrepType
  serviceType: ServiceType
}) => {
  const dispatch = useAppDispatch()
  const {
    prep_status,
    created_at,
    fire_at,
    done_at,
    completed_at,
    expected_at,
  } = orderPrep
  const orderServiceType = serviceType
  const isDelivery = orderServiceType === 'DELIVERY'
  const isFulfilled = prep_status === 'FULFILLED'
  const percent = isFulfilled
    ? statusPercentagesFulfilled[prep_status]
    : statusPercentages[prep_status]
  const createdDate = parseIsoToDate(created_at)
  const fireDate = fire_at ? parseIsoToDate(fire_at) : 0
  const progressAt = createdDate > fireDate ? created_at : fire_at

  useEffect(() => {
    if (fire_at !== null && !isFulfilled) {
      const update = setInterval(
        () => dispatch(fetchCustomerOrder(orderId)),
        15000
      )
      return () => clearInterval(update)
    }
  }, [dispatch, orderId, fire_at, isFulfilled])

  if (!fire_at) return null

  return (
    <OrderPrepView>
      <OrderPrepContainer>
        <OrderPrepProgress>
          <OrderProgress
            prepStatus={prep_status}
            isFulfilled={isFulfilled}
            statusPercentages={statusPercentages}
            statusPercentagesFulfilled={statusPercentagesFulfilled}
          />
        </OrderPrepProgress>
        <OrderPrepStep title="Order submitted" timestamp={created_at} />
        <OrderPrepStep
          title="Order received"
          timestamp={created_at}
          isActive={prep_status === 'TODO'}
        />
        <OrderPrepStep
          title="Preparing your order"
          timestamp={percent >= 50 ? progressAt : null}
          isActive={prep_status === 'IN_PROGRESS'}
        />
        <OrderPrepStep
          title="Double checking your order"
          timestamp={done_at}
          isActive={prep_status === 'DONE'}
        />
        <OrderPrepStep
          title={
            isDelivery ? 'Order is on the way!' : 'Order ready for pickup!'
          }
          timestamp={completed_at}
          isActive={prep_status === 'COMPLETED'}
        />
        {isFulfilled && (
          <OrderPrepStep
            title={
              isDelivery
                ? 'Order has been delivered!'
                : 'Order has been picked up!'
            }
            timestamp={expected_at}
            isActive={prep_status === 'FULFILLED'}
          />
        )}
      </OrderPrepContainer>
    </OrderPrepView>
  )
}

export default OrderPrep
